import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, FileStack, Send, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { listCasesByOwner, listApplications, getCasePrivateOwner } from "../services/cases";
import { getUnreadMap } from "../services/reads";
import { CaseCard } from "../components/CaseCard";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

const StatCard = ({ icon: Icon, label, value, tint }) => (
  <div className="rounded-lg border border-border bg-card p-5">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{label}</p>
        <p className="mt-1 font-heading text-3xl font-bold">{value}</p>
      </div>
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tint}`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

export default function LabDashboard() {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const [cases, setCases] = useState([]);
  const [appCounts, setAppCounts] = useState({});
  const [unread, setUnread] = useState({});
  const [patients, setPatients] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const list = await listCasesByOwner(user.uid);
        setCases(list);
        // Count interested designers per open case (Published / Designer Selected).
        const open = list.filter((c) => c.status === "Published" || c.status === "Designer Selected");
        const entries = await Promise.all(
          open.map(async (c) => [c.id, (await listApplications(c.id).catch(() => [])).length])
        );
        setAppCounts(Object.fromEntries(entries));
        getUnreadMap(list, user.uid).then(setUnread).catch(() => {});
        // Owner can read each case's private data to show the patient name on the board.
        Promise.all(list.map(async (c) => [c.id, (await getCasePrivateOwner(c.id))?.patientName || ""]))
          .then((ps) => setPatients(Object.fromEntries(ps)))
          .catch(() => {});
      } catch {
        setCases([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const stats = {
    total: cases.length,
    published: cases.filter((c) => c.status === "Published").length,
    inProgress: cases.filter((c) => ["In Progress", "Designer Selected", "Submitted"].includes(c.status)).length,
    completed: cases.filter((c) => c.status === "Completed").length,
  };

  const activeCases = cases.filter((c) => c.status !== "Completed");
  const completedCases = cases.filter((c) => c.status === "Completed");

  const renderGrid = (list, testid) => (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid={testid}>
      {list.map((c, i) => <CaseCard key={c.id} caseItem={c} index={i} applicantCount={appCounts[c.id] || 0} unreadCount={unread[c.id] || 0} patientName={patients[c.id] || ""} />)}
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="lab-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t("lab.dashboardTitle")}</h1>
          <p className="mt-1 text-muted-foreground">{t("lab.dashboardSubtitle")}</p>
        </div>
        <Link to="/lab/new">
          <Button size="lg" className="rounded-full bg-brand text-base font-semibold text-white hover:bg-brand/90" data-testid="new-case-button">
            <Plus className="mr-1.5 h-5 w-5" /> {t("lab.newCase")}
          </Button>
        </Link>
      </div>

      {profile?.role === "lab" && profile?.profileComplete === false && (
        <div
          className="mt-6 flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between"
          data-testid="complete-profile-banner"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-900">{t("lab.completeProfileTitle")}</p>
              <p className="text-sm text-amber-800">{t("lab.completeProfileDesc")}</p>
            </div>
          </div>
          <Link to="/lab/account">
            <Button size="sm" className="whitespace-nowrap rounded-full bg-amber-600 text-white hover:bg-amber-700" data-testid="complete-profile-cta">
              {t("lab.completeProfileCta")}
            </Button>
          </Link>
        </div>
      )}

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={FileStack} label={t("lab.totalCases")} value={stats.total} tint="bg-slate-100 text-slate-700" />
        <StatCard icon={Send} label={t("lab.published")} value={stats.published} tint="bg-blue-50 text-blue-700" />
        <StatCard icon={Loader2} label={t("lab.inProgress")} value={stats.inProgress} tint="bg-amber-50 text-amber-700" />
        <StatCard icon={CheckCircle2} label={t("lab.completed")} value={stats.completed} tint="bg-emerald-50 text-emerald-700" />
      </div>

      <h2 className="mb-4 mt-12 font-heading text-xl font-semibold tracking-tight">{t("lab.activeCases")}</h2>
      <div className="mt-4">
        {loading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-72 w-full rounded-lg" />)}
          </div>
        ) : cases.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="no-cases">
            {t("lab.noCases")}
          </div>
        ) : activeCases.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="no-active-cases">
            {t("lab.noActiveCases")}
          </div>
        ) : (
          renderGrid(activeCases, "active-cases-grid")
        )}
      </div>

      {!loading && completedCases.length > 0 && (
        <>
          <h2 className="mb-4 mt-12 flex items-center gap-2 font-heading text-xl font-semibold tracking-tight">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" /> {t("lab.completedCases")}
          </h2>
          <div className="mt-4">{renderGrid(completedCases, "completed-cases-grid")}</div>
        </>
      )}
    </div>
  );
}
